import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { filesApi } from "@/lib/humhub-api/files";

interface UseFileByGuidReturn {
  fileBlobUrl: string | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export const usePublicFileByGuid = (
  guid: string | null | undefined,
): UseFileByGuidReturn => {
  const [fileBlobUrl, setFileBlobUrl] = useState<string | null>(null);

  // React Queryでファイル取得
  const {
    data: blob,
    isLoading,
    error: queryError,
    refetch: queryRefetch,
  } = useQuery({
    queryKey: ["fileBlob", guid],
    queryFn: async () => {
      if (!guid) {
        throw new Error("File GUID is null or undefined");
      }

      const blob = await filesApi.getPublicFileByGuid(guid);

      if (blob.size === 0) {
        throw new Error("Received empty blob");
      }

      return blob;
    },
    enabled: !!guid, // guidがある場合のみクエリ実行
    staleTime: 10 * 60 * 1000, // ファイルは10分間キャッシュ
    gcTime: 30 * 60 * 1000, // 30分間メモリに保持
    // Blobオブジェクトは参照で比較するため、常に新しいものとして扱う
    structuralSharing: false,
  });

  // BlobからURLを生成し、クリーンアップを管理
  useEffect(() => {
    if (!blob) {
      setFileBlobUrl(null);
      return;
    }

    // 新しいBlobURLを生成
    const url = URL.createObjectURL(blob);
    setFileBlobUrl(url);

    // クリーンアップ: コンポーネントアンマウント時または新しいblobが来た時
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [blob]);

  const refetch = () => {
    // 既存のBlobURLを削除してからリフェッチ
    queryRefetch();
  };

  return {
    fileBlobUrl,
    isLoading,
    error: queryError instanceof Error ? queryError : null,
    refetch,
  };
};
